import proj4 from "proj4";
import JSZip from "./lib/jszip";
import { SHPParser, DBFParser } from "./preprocess";

const EPSG4326 = proj4('EPSG:4326');

const PROJ_DEFS = {
	'27700': '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +datum=OSGB36 +towgs84=446.448,-125.157,542.06 +units=m +no_defs',
	'32628': '+proj=utm +zone=28 +datum=WGS84 +units=m +no_defs',
	'3857': '+proj=merc +lon_0=0 +k=1 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs',
	'3821': '+proj=tmerc +ellps=GRS67 +towgs84=-752,-358,-179,-.0000011698,.0000018398,.0000009822,.00002329 +lat_0=0 +lon_0=121 +x_0=250000 +y_0=0 +k=0.9999 +units=m +no_defs'
};

export function loadshp(config, returnData) {
	const inputData = {};
	const url = config.url;
	const encoding = config.encoding || 'utf-8';
	
	let EPSGUser, EPSGOutput;
	
	function TransCoord(x, y) {
		const px = parseFloat(x);
		const py = parseFloat(y);
	
		// Skip transformation if EPSG is the same
		if (EPSGUser === EPSGOutput) {
			return { x: px, y: py };
		}
	
		try {
			if (EPSGUser && EPSGOutput) {
				const [lon, lat] = proj4(EPSGUser, EPSGOutput, [px, py]);
				return { x: lon, y: lat };
			}
		} catch (e) {
			console.warn('Projection error:', e);
		}
	
		return { x: px, y: py };
	}
	
	function shpLoader(data, returnData) {
		inputData.shp = data;
		if (inputData.shp && inputData.dbf && returnData)
			returnData(toGeojson(inputData));
	}
	
	function dbfLoader(data, returnData) {
		inputData.dbf = data;
		if (inputData.shp && inputData.dbf && returnData)
			returnData(toGeojson(inputData));
	}
	
	function toGeojson({ shp, dbf }) {
		const geojson = {
			type: "FeatureCollection",
			features: [],
			bbox: []
		};
		
		if (outputEPSG === '27700') {
			geojson.crs = {
				type: 'name',
				properties: {
					name: 'urn:ogc:def:crs:EPSG::27700'
				}
			};
		}
	
		const min = TransCoord(shp.minX, shp.minY);
		const max = TransCoord(shp.maxX, shp.maxY);
		geojson.bbox = [min.x, min.y, max.x, max.y];
	
		const shpRecords = shp.records;
		const dbfRecords = dbf.records;
	
		for (let i = 0; i < shpRecords.length; i++) {
			const feature = {
				type: 'Feature',
				properties: dbfRecords[i],
				geometry: {}
			};
	
			const shape = shpRecords[i].shape;
			const type = shape.type;
	
			switch (type) {
				case 1: // Point
					feature.geometry.type = "Point";
					const pt = TransCoord(shape.content.x, shape.content.y);
					feature.geometry.coordinates = [pt.x, pt.y];
					break;
				case 3: // Polyline
				case 8: // MultiPoint
					feature.geometry.type = (type === 3) ? "LineString" : "MultiPoint";
					feature.geometry.coordinates = [];
					for (let j = 0; j < shape.content.points.length; j += 2) {
						const pt = TransCoord(shape.content.points[j], shape.content.points[j + 1]);
						feature.geometry.coordinates.push([pt.x, pt.y]);
					}
					break;
				case 5: // Polygon
					feature.geometry.type = "Polygon";
					feature.geometry.coordinates = [];
					for (let p = 0; p < shape.content.parts.length; p++) {
						const partIndex = shape.content.parts[p];
						const nextPartIndex = shape.content.parts[p + 1] ?? (shape.content.points.length / 2);
						const part = [];
						for (let j = partIndex * 2; j < nextPartIndex * 2; j += 2) {
							const pt = TransCoord(shape.content.points[j], shape.content.points[j + 1]);
							part.push([pt.x, pt.y]);
						}
						feature.geometry.coordinates.push(part);
					}
					break;
				default:
					continue;
			}
	
			if ("coordinates" in feature.geometry) geojson.features.push(feature);
		}
	
		return geojson;
	}
	
	const inputEPSG = String(config.EPSG || 4326);
	const outputEPSG = String(config.outputEPSG || 4326);

	// Define known projections
	if (PROJ_DEFS[inputEPSG]) proj4.defs('EPSG:' + inputEPSG, PROJ_DEFS[inputEPSG]);
	if (PROJ_DEFS[outputEPSG]) proj4.defs('EPSG:' + outputEPSG, PROJ_DEFS[outputEPSG]);
	
	try {
		if (inputEPSG === outputEPSG) {
			EPSGUser = EPSGOutput = proj4('EPSG:' + inputEPSG);
		} else {
			EPSGUser = proj4('EPSG:' + inputEPSG);
			EPSGOutput = proj4('EPSG:' + outputEPSG);
		}
	} catch (e) {
		console.error('Failed to define projections:', e);
		EPSGUser = EPSGOutput = proj4('EPSG:4326');
	}
	
	const handleZip = (zip) => {
		try {
			const shpFile = zip.file(/\.shp$/i)?.[0];
			const dbfFile = zip.file(/\.dbf$/i)?.[0];
			const prjFile = zip.file(/\.prj$/i)?.[0];

			if (!shpFile || !dbfFile) throw new Error('Missing .shp or .dbf file in archive.');

			// Try to override EPSGUser with PRJ if available
			if (prjFile) {
				const prjText = prjFile.asText();
				
				try {
					proj4.defs('EPSGUSER', prjText);
					
					if (inputEPSG === outputEPSG) {
						EPSGUser = EPSGOutput = proj4('EPSGUSER'); // override
					}else{
						EPSGUser = proj4('EPSGUSER');
					}
					
					console.log('Overriding EPSGUser from .prj file');
				} catch (e) {
					console.warn('Invalid .prj projection string:', e);
				}
			}
			
			console.log('EPSGUser', EPSGUser);
			console.log('EPSGOutput', EPSGOutput);

			const URL = window.URL || window.webkitURL;
			SHPParser.load(URL.createObjectURL(new Blob([shpFile.asArrayBuffer()])), shpLoader, returnData);
			DBFParser.load(URL.createObjectURL(new Blob([dbfFile.asArrayBuffer()])), encoding, dbfLoader, returnData);
		} catch (e) {
			console.error('Failed to parse ZIP:', e);
			returnData(false);
		}
	};

	if (typeof url !== 'string') {
		const reader = new FileReader();
		reader.onload = e => {
			const zip = new JSZip(e.target.result);
			handleZip(zip);
		};
		reader.readAsArrayBuffer(url);
	} else {
		JSZipUtils.getBinaryContent(url, (err, data) => {
			if (err) return returnData(false);
			const zip = new JSZip(data);
			handleZip(zip);
		});
	}
}